import { useTranslation } from '@repo/i18n'
import type { ReactNode, Ref } from 'react'
import type { AsyncPickerSource } from '#commons/async-pickers'
import { InfiniteVirtualList } from '#commons/infinite-virtual-list'
import { VirtualList } from '#commons/virtual-list'
import { ComboboxEmpty, ComboboxItem, ComboboxList } from '#components/combobox'
import { Skeleton } from '#components/skeleton'
import type { VirtualizationApi } from '#hooks/use-virtualization'

// The starter picker results also accept already-loaded choices without a query.
export function AsyncPickerResults<T>(props: {
	source: AsyncPickerSource<T>
	items: T[]
	getKey: (item: T) => string
	getLabel: (item: T) => string
	empty?: ReactNode
	renderOption?: (item: T) => ReactNode
	isItemDisabled: (item: T) => boolean
	ref?: Ref<VirtualizationApi>
}) {
	const translation = useTranslation()
	const empty = (
		<div className='flex h-9 items-center justify-center text-muted-foreground text-sm'>
			{props.empty ?? translation.t('shared:async_pickers.empty')}
		</div>
	)
	const error = (
		<div className='flex h-9 items-center justify-center text-destructive text-sm'>
			{translation.t('shared:async_pickers.error')}
		</div>
	)
	const renderItem = (item: T, index: number) => (
		<ComboboxItem
			disabled={props.isItemDisabled(item)}
			index={index}
			value={item}
		>
			{props.renderOption?.(item) ?? (
				<span className='min-w-0 truncate' title={props.getLabel(item)}>
					{props.getLabel(item)}
				</span>
			)}
		</ComboboxItem>
	)

	if (props.items.length === 0) {
		const pending =
			'query' in props.source
				? props.source.query.isPending
				: props.source.isPending
		const failed =
			'query' in props.source
				? props.source.query.isError
				: props.source.isError
		// Base UI needs Empty registered to keep Escape inside an empty popup.
		if (pending) {
			return (
				<ComboboxList>
					<ComboboxEmpty className='block w-full p-0 group-data-empty/combobox-content:block'>
						<PickerSkeletons />
					</ComboboxEmpty>
				</ComboboxList>
			)
		}
		return (
			<ComboboxList>
				<ComboboxEmpty className='block w-full p-0 group-data-empty/combobox-content:block'>
					{failed ? error : empty}
				</ComboboxEmpty>
			</ComboboxList>
		)
	}

	return (
		<ComboboxList className='flex max-h-none min-h-0 flex-col overflow-hidden'>
			{'query' in props.source ? (
				<InfiniteVirtualList
					className='h-[min(var(--virtual-list-size),--spacing(72))] min-h-0 shrink'
					empty={empty}
					error={error}
					estimateSize={() => PICKER_OPTION_HEIGHT}
					getItemKey={props.getKey}
					items={props.items}
					query={props.source.query}
					ref={props.ref}
					skeleton={<PickerSkeletons />}
				>
					{renderItem}
				</InfiniteVirtualList>
			) : (
				<VirtualList
					className='h-[min(var(--virtual-list-size),--spacing(72))] min-h-0 shrink'
					estimateSize={() => PICKER_OPTION_HEIGHT}
					footer={props.source.isError ? error : undefined}
					getItemKey={props.getKey}
					items={props.items}
					ref={props.ref}
				>
					{renderItem}
				</VirtualList>
			)}
		</ComboboxList>
	)
}

function PickerSkeletons() {
	return (
		<div className='flex flex-col gap-1'>
			<Skeleton className='h-9' />
			<Skeleton className='h-9' />
			<Skeleton className='h-9' />
		</div>
	)
}

// Match the primitive's 20px line and 12px padding so scrolling does not resize the popup.
const PICKER_OPTION_HEIGHT = 32
